import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { errMsg, inr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { payWithRazorpay } from "@/lib/razorpay";
import { useCart } from "@/context/CartContext";

export default function CheckoutDialog({ open, onOpenChange, kind, course, title, total }) {
  const cart = useCart();
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", address: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const done = (msg) => {
    toast.success(msg);
    if (kind === "cart") {
      cart.clear();
      cart.setOpen(false);
    }
    onOpenChange(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = kind === "course"
      ? { kind, course_id: course.id, name: form.name, phone: form.phone }
      : { kind, items: cart.items.map((i) => ({ id: i.id, qty: i.qty })), name: form.name, phone: form.phone, address: form.address };
    try {
      if ((await payWithRazorpay(payload, title)) === "paid") {
        done(t("co.paid"));
      }
    } catch (err) {
      toast.error(err?.message && !err.response ? err.message : errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-parchment max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest-800">{kind === "course" ? t("co.enrol") : t("co.checkout")}</DialogTitle>
          <DialogDescription>{title} · <span className="font-semibold text-forest-800" data-testid="checkout-total">{inr(total)}</span></DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="co-name">{t("co.name")}</Label>
            <Input id="co-name" required minLength={2} value={form.name} onChange={set("name")} data-testid="checkout-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="co-phone">{t("co.phone")}</Label>
            <Input id="co-phone" required inputMode="numeric" placeholder={t("co.phonePh")} value={form.phone} onChange={set("phone")} data-testid="checkout-phone-input" />
          </div>
          {kind === "cart" && (
            <div className="space-y-1.5">
              <Label htmlFor="co-address">{t("co.address")}</Label>
              <Textarea id="co-address" required rows={3} value={form.address} onChange={set("address")} data-testid="checkout-address-input" />
            </div>
          )}
          <button disabled={busy} className="w-full flex items-center justify-center gap-2 bg-forest-800 hover:bg-forest-700 text-parchment font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60" data-testid="checkout-pay-button">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("co.pay", { amt: inr(total) })}
          </button>
          <p className="text-xs text-center text-[#8A978E]">{t("co.rzpNote")}</p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
